import React, { useState, useMemo, lazy, Suspense } from 'react';
import Container from 'components/UI/Container';
import Title from 'components/UI/Title';
import Loader from 'components/UI/Loader';
import Filters from './Filters';
import DefaultProjects from './DefaultProjects';
import { useFullProjectsCards } from 'staticQueries/project-previews';
import { useProjectGalleries } from 'staticQueries/project-galleries';
import { withErrorBoundary } from 'components/ErrorBoundary';
import { ALL_GALLERIES_KEY } from './constants';

import * as styles from './ProjectsLayout.module.scss';

const FilteredProjects = lazy(() => import('./FilteredProjects'));

const DEFAULT_STATE = {
  industries: [],
  countries: [],
};

function ProjectsLayout({ title, galleriesContent }) {
  const projects = useFullProjectsCards();
  const galleries = useProjectGalleries();
  const [filters, setFilters] = useState(DEFAULT_STATE);
  const [activeGallery, setActiveGallery] = useState(ALL_GALLERIES_KEY);
  const [fadeInAnimation, setFadeInAnimation] = useState(false);
  const activeFilters = Object.values(filters)?.flat().length;

  const handleSetFilters = payload => {
    setFilters(payload);
    setFadeInAnimation(true);
  };

  const flags = projects.reduce((acc, project) => {
    acc[project.country] = project.countryFlag;
    return acc;
  }, {});

  const filteredProjects = useMemo(() => {
    let result = [...projects];

    const { industries, countries } = filters;

    result = result.filter(project => {
      return (
        (!industries.length || industries.some(industry => project.industry === industry.value)) &&
        (!countries.length || countries.some(country => project.country === country.value))
      );
    });

    return result;
  }, [projects, filters]);

  return (
    <>
      <Container id="concepts-list" className={styles.container}>
        <div className={styles.wrapper}>
          <div className={styles.topRow}>
            <Title tag="h1" size="h1Cases" className={styles.title}>
              {title}
            </Title>
            <Filters
              projects={projects}
              flags={flags}
              defaultFilters={DEFAULT_STATE}
              activeFilters={activeFilters}
              filters={filters}
              setFilters={handleSetFilters}
            />
          </div>
          {Boolean(activeFilters) && (
            <Suspense fallback={<Loader theme="page" />}>
              <FilteredProjects
                projects={filteredProjects}
                flags={flags}
                defaultFilters={DEFAULT_STATE}
                filters={filters}
                setFilters={handleSetFilters}
                fadeInAnimation={fadeInAnimation}
                setFadeInAnimation={setFadeInAnimation}
              />
            </Suspense>
          )}
        </div>
      </Container>
      {!activeFilters && (
        <DefaultProjects
          galleries={galleries}
          galleriesContent={galleriesContent}
          activeGallery={activeGallery}
          setActiveGallery={setActiveGallery}
          fadeInAnimation={fadeInAnimation}
          setFadeInAnimation={setFadeInAnimation}
        />
      )}
    </>
  );
}

ProjectsLayout.displayName = 'ProjectsLayout';

export default withErrorBoundary(ProjectsLayout);
